/**
 * Sticky, glassy landing header — brand mark, in-page anchor nav (desktop) and
 * the language switcher (anonymous visitors: persisted in localStorage) and the
 * sign-in / sign-up CTAs. On phones the anchor links collapse into a
 * disclosure menu (pure state, no dependency). Honors the safe-area inset.
 */

import { useState } from 'react';

import { BRAND, NAV_LINKS } from '@/routes/landing/landing-data';
import { BrandMark, useLandingCta } from './shared';
import { useT } from '@/i18n';
import { LanguageSwitcher } from '@/i18n/LanguageSwitcher';

export function LandingNav() {
  const { t } = useT();
  const { signin, signup } = useLandingCta();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 um-glass-strong">
      <nav
        aria-label={t('routes.landing.sectionsLandingNav.mainNavigation')}
        className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8"
      >
        <a href="#top" className="flex items-center gap-2.5 rounded-token focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <BrandMark />
          <span className="text-lg font-bold tracking-tight text-foreground">{BRAND.name}</span>
        </a>

        {/* Desktop anchor nav */}
        <ul className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className="rounded-token px-3 py-2 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          {/* Company SSO: tenant IdPs are hidden on the shared login page. */}
          <a
            href="/sso"
            data-testid="company-sso-entry"
            className="hidden rounded-token px-3 py-2 text-sm font-medium text-muted transition-colors hover:bg-surface-2 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:inline-flex"
          >{t('shell.auth.companySso.navEntry')}</a>
          <button
            type="button"
            onClick={() => signin()}
            className="hidden rounded-token px-3 py-2 text-sm font-semibold text-foreground transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:inline-flex"
          >{t('routes.landing.sectionsLandingNav.signIn')}</button>
          <button
            type="button"
            onClick={() => signup()}
            className="inline-flex items-center rounded-token bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-elevation-1 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg motion-reduce:transition-none"
          >{t('routes.landing.sectionsLandingNav.createAnAccount')}</button>
          {/* Mobile menu toggle */}
          <button
            type="button"
            aria-expanded={open}
            aria-controls="landing-mobile-menu"
            aria-label={open ? t('routes.landing.sectionsLandingNav.closeMenu') : t('routes.landing.sectionsLandingNav.openMenu')}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-token text-foreground transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" aria-hidden="true" className="h-6 w-6">
              {open ? <path d="M6 18 18 6M6 6l12 12" /> : <path d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile disclosure menu */}
      {open && (
        <div id="landing-mobile-menu" className="border-t border-border/70 lg:hidden">
          <ul className="mx-auto flex w-full max-w-7xl flex-col gap-1 px-4 py-3 sm:px-6">
            {NAV_LINKS.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-token px-3 py-2.5 text-base font-medium text-foreground transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {link.label}
                </a>
              </li>
            ))}
            <li className="pt-1">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  signin();
                }}
                className="block w-full rounded-token px-3 py-2.5 text-left text-base font-semibold text-foreground transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >{t('routes.landing.sectionsLandingNav.signIn')}</button>
            </li>
            <li>
              <a
                href="/sso"
                onClick={() => setOpen(false)}
                className="block rounded-token px-3 py-2.5 text-base font-medium text-foreground transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >{t('shell.auth.companySso.navEntry')}</a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}
